/**
 * GuardMarker
 *
 * Map marker component for displaying a guard's location on Google Maps.
 * Color-coded based on status, with tooltip showing details.
 *
 * Popup state is controlled by parent to ensure only one popup is open at a time.
 */

import React, { memo } from 'react';
import { Box, Paper, Typography, Button, Divider, ClickAwayListener } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import BatteryAlertIcon from '@mui/icons-material/BatteryAlert';
import RouteIcon from '@mui/icons-material/Route';
import styles from './GuardMarker.module.css';
import { GuardLocation, STATUS_COLORS, STATUS_LABELS } from './types';
import classNames from 'classnames';

interface GuardMarkerProps {
  /** Guard location data */
  guard: GuardLocation;
  /** Whether this marker is currently selected */
  isSelected?: boolean;
  /** Whether the popup should be shown (controlled by parent) */
  showPopup?: boolean;
  /** Callback when marker is clicked */
  onClick?: (guardId: number) => void;
  /** Callback when "View Details" is clicked */
  onViewDetails?: (guardId: number) => void;
  /** Callback to close the popup (click away) */
  onClosePopup?: () => void;
  /** Whether the trail is currently visible for this guard */
  showTrail?: boolean;
  /** Callback when trail visibility is toggled */
  onToggleTrail?: (guardId: number) => void;
  /** Required by google-map-react for positioning */
  lat: number;
  /** Required by google-map-react for positioning */
  lng: number;
}

const GuardMarker: React.FC<GuardMarkerProps> = ({
  guard,
  isSelected = false,
  showPopup = false,
  onClick,
  onViewDetails,
  onClosePopup,
  showTrail = false,
  onToggleTrail,
}) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.(guard.guardId);
  };

  const handleViewDetails = (e: React.MouseEvent) => {
    e.stopPropagation();
    onViewDetails?.(guard.guardId);
  };

  const handleToggleTrail = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleTrail?.(guard.guardId);
  };

  const handleClickAway = () => {
    if (showPopup) {
      onClosePopup?.();
    }
  };

  const statusColor = STATUS_COLORS[guard.status];
  const isLowBattery = guard.batteryPercent !== null && guard.batteryPercent < 20;

  // Map status to CSS class names for animations
  const statusClassName = {
    active: styles.statusActive,
    stale: styles.statusStale,
    offline: styles.statusOffline,
    not_on_patrol: styles.statusNotOnPatrol,
  }[guard.status];

  const markerContent = (
    <div
      className={classNames(styles.markerContainer, {
        [styles.selected]: isSelected,
      })}
    >
      {/* Marker Pin */}
      <div
        className={classNames(styles.markerPin, statusClassName, {
          [styles.selected]: isSelected,
        })}
        style={{ backgroundColor: statusColor, color: statusColor }}
        onClick={handleClick}
        title={guard.name}
      >
        <PersonIcon className={styles.markerIcon} />
      </div>

      {/* Popup */}
      {showPopup && (
        <Paper className={styles.popup} elevation={4}>
          {/* Header */}
          <Box className={styles.popupHeader}>
            <Box
              className={styles.popupStatusDot}
              style={{ backgroundColor: statusColor }}
            />
            <Typography variant="subtitle1" className={styles.popupName}>
              {guard.name}
            </Typography>
          </Box>

          {/* Status Row */}
          <Box className={styles.popupRow}>
            <Typography variant="body2" color="textSecondary">
              Status:
            </Typography>
            <Typography
              variant="body2"
              className={classNames(styles.popupValue, styles[`status_${guard.status}`])}
            >
              {STATUS_LABELS[guard.status]}
            </Typography>
          </Box>

          {/* Last Seen Row */}
          <Box className={styles.popupRow}>
            <Typography variant="body2" color="textSecondary">
              Last seen:
            </Typography>
            <Typography variant="body2" className={styles.popupValue}>
              {guard.lastSeenFormatted}
            </Typography>
          </Box>

          {/* Current Patrol */}
          {guard.currentPatrol && (
            <>
              <Divider className={styles.popupDivider} />
              <Box className={styles.popupRow}>
                <Typography variant="body2" color="textSecondary">
                  Patrol:
                </Typography>
                <Typography variant="body2" className={styles.popupValue}>
                  {guard.currentPatrol.customerName}
                </Typography>
              </Box>
              <Box className={styles.popupRow}>
                <Typography variant="body2" color="textSecondary">
                  Site:
                </Typography>
                <Typography variant="body2" className={styles.popupValue}>
                  {guard.currentPatrol.siteName}
                </Typography>
              </Box>
            </>
          )}

          {/* Device Info */}
          {guard.deviceInfo && (
            <Box className={styles.popupRow}>
              <Typography variant="body2" color="textSecondary">
                Device:
              </Typography>
              <Typography variant="body2" className={styles.popupValue}>
                {guard.deviceInfo}
              </Typography>
            </Box>
          )}

          {/* Battery */}
          {guard.batteryPercent !== null && (
            <Box className={styles.popupRow}>
              <Typography variant="body2" color="textSecondary">
                Battery:
              </Typography>
              <Box className={styles.batteryDisplay}>
                {isLowBattery ? (
                  <BatteryAlertIcon className={styles.batteryIconLow} />
                ) : (
                  <BatteryFullIcon className={styles.batteryIcon} />
                )}
                <Typography
                  variant="body2"
                  className={classNames(styles.popupValue, {
                    [styles.batteryLow]: isLowBattery,
                  })}
                >
                  {guard.batteryPercent}%
                </Typography>
              </Box>
            </Box>
          )}

          {/* Actions */}
          <Box className={styles.popupActions}>
            <Button
              variant="contained"
              size="small"
              fullWidth
              onClick={handleViewDetails}
            >
              View Details
            </Button>
            {onToggleTrail && (
              <Button
                variant={showTrail ? 'contained' : 'outlined'}
                size="small"
                fullWidth
                onClick={handleToggleTrail}
                startIcon={<RouteIcon />}
                color={showTrail ? 'primary' : 'inherit'}
                sx={{ mt: 1 }}
              >
                {showTrail ? 'Hide Trail' : 'Show Trail'}
              </Button>
            )}
          </Box>
        </Paper>
      )}
    </div>
  );

  // Wrap with ClickAwayListener only when popup is shown
  if (showPopup) {
    return (
      <ClickAwayListener onClickAway={handleClickAway}>
        {markerContent}
      </ClickAwayListener>
    );
  }

  return markerContent;
};

/**
 * Custom comparison function for React.memo
 * Only re-render when values that actually affect the marker change
 */
function arePropsEqual(
  prevProps: GuardMarkerProps,
  nextProps: GuardMarkerProps
): boolean {
  // Compare UI state
  if (
    prevProps.isSelected !== nextProps.isSelected ||
    prevProps.showPopup !== nextProps.showPopup ||
    prevProps.showTrail !== nextProps.showTrail
  ) {
    return false;
  }

  // Compare guard data by relevant values (not reference)
  const prevGuard = prevProps.guard;
  const nextGuard = nextProps.guard;

  if (
    prevGuard.guardId !== nextGuard.guardId ||
    prevGuard.name !== nextGuard.name ||
    prevGuard.status !== nextGuard.status ||
    prevGuard.latitude !== nextGuard.latitude ||
    prevGuard.longitude !== nextGuard.longitude ||
    prevGuard.batteryPercent !== nextGuard.batteryPercent ||
    prevGuard.lastSeenFormatted !== nextGuard.lastSeenFormatted ||
    prevGuard.deviceInfo !== nextGuard.deviceInfo
  ) {
    return false;
  }

  // Compare current patrol if present
  const prevPatrol = prevGuard.currentPatrol;
  const nextPatrol = nextGuard.currentPatrol;

  if (!prevPatrol && !nextPatrol) return true;
  if (!prevPatrol || !nextPatrol) return false;

  if (
    prevPatrol.customerName !== nextPatrol.customerName ||
    prevPatrol.siteName !== nextPatrol.siteName
  ) {
    return false;
  }

  return true;
}

export default memo(GuardMarker, arePropsEqual);
